<template>
    <div class="searchable-dropdown" :class="{ 'is-open': isOpen }">
        <div class="searchable-dropdown-trigger" @mousedown.prevent="handleTriggerMouseDown">
            <input
                ref="searchInput"
                type="text"
                :value="displayValue"
                :placeholder="effectivePlaceholder"
                @input="handleSearch"
                @focus="openDropdown"
                @blur="handleBlur"
                @keydown="handleKeyDown"
                @mousedown.stop
                class="searchable-dropdown-input"
                :disabled="effectiveDisabled"
            />
            <span class="searchable-dropdown-arrow" :class="{ 'is-open': isOpen }">▼</span>
        </div>
        <div v-if="isOpen" class="searchable-dropdown-menu" ref="menuRef" @scroll="handleScroll">
            <div v-if="filteredItems.length === 0 && !isLoadingMore" class="searchable-dropdown-no-results">
                No results found
            </div>
            <div
                v-for="(item, index) in filteredItems"
                :key="getItemKey(item)"
                :ref="el => setOptionRef(el, index)"
                class="searchable-dropdown-option"
                :class="{ 
                    'is-selected': isItemSelected(item),
                    'is-highlighted': highlightedIndex === index
                }"
                @mousedown.prevent="handleItemMouseDown(item)"
                @mouseenter="highlightedIndex = index"
            >
                <slot name="option" :option="item">
                    {{ getItemLabel(item) }}
                </slot>
            </div>
            <div v-if="isLoadingMore" class="searchable-dropdown-loading">
                <span class="searchable-dropdown-spinner"></span>
                Loading more...
            </div>
            <div v-if="hasMore && !isLoadingMore && filteredItems.length > 0" class="searchable-dropdown-load-more">
                Scroll for more
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, ComponentPublicInstance, onMounted, onUnmounted } from 'vue'

interface DropdownOptions {
    searchable?: boolean
    disabled?: boolean
    sortBy?: string | ((option: any) => string | number)
    placeholder?: string
    // Lazy loading options
    lazyLoad?: boolean
    loadFunction?: (page: number, searchText: string) => Promise<any[]>
    pageSize?: number
}

interface Props {
    modelValue: any
    list: any[]
    optionKey: (option: any) => string | number
    optionLabel?: string | ((option: any) => string)
    placeholder?: string
    options?: DropdownOptions
}

const props = withDefaults(defineProps<Props>(), {
    placeholder: 'Select an option...',
    optionLabel: 'label',
    options: () => ({
        searchable: true,
        disabled: false,
        sortBy: undefined,
        placeholder: undefined
    })
})

const emit = defineEmits<{
    'update:modelValue': [value: any]
}>()

const isOpen = ref(false)
const searchQuery = ref('')
const searchInput = ref<HTMLInputElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)
const isClickingItem = ref(false)
const isOpening = ref(false)
const highlightedIndex = ref<number>(-1)
const optionRefs = ref<(HTMLElement | null)[]>([])

// Lazy loading state
const isLoadingMore = ref(false)
const currentPage = ref(1)
const hasMore = ref(true)
const allLoadedItems = ref<any[]>([])
const debounceTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

// Computed properties for dropdown configuration
const effectiveDisabled = computed(() => props.options?.disabled ?? false)
const effectiveSearchable = computed(() => props.options?.searchable ?? true)
const effectiveSortBy = computed(() => props.options?.sortBy)
const effectivePlaceholder = computed(() => props.options?.placeholder ?? props.placeholder)
const isLazyLoad = computed(() => props.options?.lazyLoad ?? false)
const loadFunction = computed(() => props.options?.loadFunction)
const pageSize = computed(() => props.options?.pageSize ?? 10)

// Sort items in the list
const sortedItems = computed(() => {
    // If lazy loading, use allLoadedItems instead of props.list
    const itemsToSort = isLazyLoad.value ? allLoadedItems.value : (props.list || [])
    
    if (itemsToSort.length === 0) return []
    if (!effectiveSortBy.value) return itemsToSort
    
    const sorted = [...itemsToSort]
    sorted.sort((a, b) => {
        const aValue = typeof effectiveSortBy.value === 'function' 
            ? effectiveSortBy.value(a) 
            : a[effectiveSortBy.value]
        const bValue = typeof effectiveSortBy.value === 'function' 
            ? effectiveSortBy.value(b) 
            : b[effectiveSortBy.value]
        
        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0
        if (aValue == null) return 1
        if (bValue == null) return -1
        
        if (aValue < bValue) return -1
        if (aValue > bValue) return 1
        return 0
    })
    
    return sorted
})

// Filter items based on search query
const filteredItems = computed(() => {
    // If lazy loading, use allLoadedItems instead of props.list
    const itemsToFilter = isLazyLoad.value ? allLoadedItems.value : sortedItems.value
    
    if (!effectiveSearchable.value || !searchQuery.value.trim()) {
        return itemsToFilter
    }
    
    const query = searchQuery.value.toLowerCase().trim()
    return itemsToFilter.filter(item => {
        const label = getItemLabel(item).toLowerCase()
        return label.includes(query)
    })
})

// Get display value for the input
const displayValue = computed(() => {
    if (effectiveSearchable.value && isOpen.value) {
        return searchQuery.value
    }
    
    if (!props.modelValue) return ''
    
    const selectedItem = sortedItems.value.find(item => 
        getItemKey(item) === getItemKey(props.modelValue)
    )
    
    return selectedItem ? getItemLabel(selectedItem) : ''
})

// Helper functions
const getItemKey = (item: any): string | number => {
    return props.optionKey(item)
}

const getItemLabel = (item: any): string => {
    if (typeof props.optionLabel === 'function') {
        return props.optionLabel(item)
    }
    return item[props.optionLabel] ?? String(item)
}

const isItemSelected = (item: any): boolean => {
    if (!props.modelValue) return false
    return getItemKey(item) === getItemKey(props.modelValue)
}

// Event handlers
const handleTriggerMouseDown = (event: MouseEvent) => {
    if (effectiveDisabled.value) return
    // If clicking on the input itself, let the focus handler take care of it
    if ((event.target as HTMLElement).tagName === 'INPUT') {
        return
    }
    event.preventDefault()
    // Only open if closed, don't toggle
    if (!isOpen.value) {
        isOpening.value = true
        isOpen.value = true
        nextTick(() => {
            searchInput.value?.focus()
            setTimeout(() => {
                isOpening.value = false
            }, 100)
        })
    }
}

const openDropdown = () => {
    if (effectiveDisabled.value) return
    // Only open if not already open to prevent flickering
    if (!isOpen.value) {
        isOpening.value = true
        isOpen.value = true
        // Clear search query when opening to allow fresh search
        searchQuery.value = ''
        highlightedIndex.value = -1
        
        // If lazy loading and no items loaded yet, load initial page
        if (isLazyLoad.value && allLoadedItems.value.length === 0 && loadFunction.value) {
            currentPage.value = 1
            hasMore.value = true
            loadMoreItems()
        }
        
        setTimeout(() => {
            isOpening.value = false
        }, 100)
    }
    // Select all text if there's a value, so user can start typing immediately
    nextTick(() => {
        if (searchInput.value && props.modelValue) {
            searchInput.value.select()
        }
    })
}

// Lazy loading functions
const loadMoreItems = async () => {
    if (!loadFunction.value || isLoadingMore.value || !hasMore.value) return
    
    isLoadingMore.value = true
    try {
        const results = await loadFunction.value(currentPage.value, searchQuery.value)
        if (results && results.length > 0) {
            allLoadedItems.value.push(...results)
            // If we got fewer items than page size, no more pages
            if (results.length < pageSize.value) {
                hasMore.value = false
            } else {
                currentPage.value++
            }
        } else {
            hasMore.value = false
        }
    } catch (error) {
        console.error('Error loading items:', error)
        hasMore.value = false
    } finally {
        isLoadingMore.value = false
    }
}

const handleScroll = (event: Event) => {
    if (!isLazyLoad.value || !hasMore.value || isLoadingMore.value) return
    
    const target = event.target as HTMLElement
    const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight
    
    // Load more when within 50px of bottom
    if (scrollBottom < 50) {
        loadMoreItems()
    }
}

const handleBlur = (event: FocusEvent) => {
    // Don't close if we're clicking on an item or currently opening
    if (isClickingItem.value || isOpening.value) {
        return
    }
    
    // Delay to allow click events to fire first
    setTimeout(() => {
        // Don't close if we're still opening
        if (isOpening.value) {
            return
        }
        // Check if focus moved to an element within the dropdown
        const target = event.relatedTarget as HTMLElement
        if (!target || !target.closest('.searchable-dropdown')) {
            isOpen.value = false
            // Only clear search if we have a selected value, otherwise keep it for user to see
            if (props.modelValue) {
                searchQuery.value = ''
            }
        }
    }, 150)
}

const handleSearch = (event: Event) => {
    const target = event.target as HTMLInputElement
    const newQuery = target.value
    searchQuery.value = newQuery
    
    // Reset highlighted index when searching
    highlightedIndex.value = -1
    
    // If lazy loading, debounce and reload
    if (isLazyLoad.value && loadFunction.value) {
        if (debounceTimeout.value) {
            clearTimeout(debounceTimeout.value)
        }
        debounceTimeout.value = setTimeout(() => {
            currentPage.value = 1
            hasMore.value = true
            allLoadedItems.value = []
            loadMoreItems()
        }, 300)
    }
}

    const setOptionRef = (el: Element | ComponentPublicInstance | null, index: number) => {
    if (!(el instanceof HTMLElement)) return

    if (el) {
        optionRefs.value[index] = el
    } else {
        // Remove ref if element is removed
        if (optionRefs.value[index]) {
            optionRefs.value[index] = null
        }
    }
}

const scrollToHighlighted = () => {
    nextTick(() => {
        if (highlightedIndex.value >= 0 && optionRefs.value[highlightedIndex.value]) {
            optionRefs.value[highlightedIndex.value]?.scrollIntoView({
                block: 'nearest',
                behavior: 'smooth'
            })
        }
    })
}

const handleKeyDown = (event: KeyboardEvent) => {
    if (!isOpen.value) {
        // Open dropdown on arrow down
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            openDropdown()
            nextTick(() => {
                if (filteredItems.value.length > 0) {
                    highlightedIndex.value = 0
                    scrollToHighlighted()
                }
            })
        }
        return
    }

    const items = filteredItems.value
    if (items.length === 0) return

    switch (event.key) {
        case 'ArrowDown':
            event.preventDefault()
            highlightedIndex.value = highlightedIndex.value < items.length - 1 
                ? highlightedIndex.value + 1 
                : 0
            scrollToHighlighted()
            break

        case 'ArrowUp':
            event.preventDefault()
            highlightedIndex.value = highlightedIndex.value > 0 
                ? highlightedIndex.value - 1 
                : items.length - 1
            scrollToHighlighted()
            break

        case 'Enter':
            event.preventDefault()
            if (highlightedIndex.value >= 0 && highlightedIndex.value < items.length) {
                selectItem(items[highlightedIndex.value])
            } else if (items.length === 1) {
                // If only one item, select it
                selectItem(items[0])
            }
            break

        case 'Tab':
            // Allow tab to select if something is highlighted
            if (highlightedIndex.value >= 0 && highlightedIndex.value < items.length) {
                event.preventDefault()
                selectItem(items[highlightedIndex.value])
            }
            // Otherwise let tab work normally (close dropdown)
            break

        case 'Escape':
            event.preventDefault()
            isOpen.value = false
            highlightedIndex.value = -1
            searchInput.value?.blur()
            break
    }
}

const handleItemMouseDown = (item: any) => {
    isClickingItem.value = true
    selectItem(item)
    // Reset flag after a short delay
    setTimeout(() => {
        isClickingItem.value = false
    }, 200)
}

const selectItem = (item: any) => {
    emit('update:modelValue', item)
    searchQuery.value = ''
    isOpen.value = false
    highlightedIndex.value = -1
    // Blur the input after selection
    nextTick(() => {
        searchInput.value?.blur()
    })
}

// Watch for external modelValue changes to clear search
watch(() => props.modelValue, () => {
    if (!isOpen.value) {
        searchQuery.value = ''
        highlightedIndex.value = -1
    }
})

// Reset highlighted index when filtered items change
watch(() => filteredItems.value, () => {
    if (highlightedIndex.value >= filteredItems.value.length) {
        highlightedIndex.value = -1
    }
    // Reset option refs when items change
    nextTick(() => {
        optionRefs.value = []
    })
})

// Watch for list changes when not using lazy loading
watch(() => props.list, () => {
    if (!isLazyLoad.value) {
        allLoadedItems.value = []
    }
}, { deep: true })

// Cleanup
onUnmounted(() => {
    if (debounceTimeout.value) {
        clearTimeout(debounceTimeout.value)
    }
})
</script>

<style scoped>
.searchable-dropdown {
    position: relative;
    width: 100%;
}

.searchable-dropdown-trigger {
    position: relative;
    display: flex;
    align-items: center;
}

.searchable-dropdown-input {
    width: 100%;
    padding: 8px 28px 8px 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 14px;
    background: white;
    cursor: pointer;
    line-height: 1.5;
    box-sizing: border-box;
}

.searchable-dropdown-input:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.searchable-dropdown-input:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
}

.searchable-dropdown-arrow {
    position: absolute;
    right: 8px;
    pointer-events: none;
    color: #666;
    font-size: 10px;
    transition: transform 0.2s;
}

.searchable-dropdown-arrow.is-open {
    transform: rotate(180deg);
}

.searchable-dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 4px;
    background: white;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    max-height: 300px;
    overflow-y: auto;
    z-index: 1000;
}

.searchable-dropdown-option {
    padding: 8px 12px;
    cursor: pointer;
    transition: background-color 0.15s;
}

.searchable-dropdown-option:hover {
    background-color: #f5f5f5;
}

.searchable-dropdown-option.is-highlighted {
    background-color: #e7f3ff;
}

.searchable-dropdown-option.is-selected {
    background-color: #e7f3ff;
    font-weight: 500;
}

.searchable-dropdown-option.is-highlighted.is-selected {
    background-color: #cfe2ff;
}

.searchable-dropdown-no-results {
    padding: 12px;
    text-align: center;
    color: #999;
    font-style: italic;
}

.searchable-dropdown-loading {
    padding: 12px;
    text-align: center;
    color: #666;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.searchable-dropdown-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid #e0e0e0;
    border-top-color: #2196F3;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

.searchable-dropdown-load-more {
    padding: 8px;
    text-align: center;
    color: #999;
    font-size: 12px;
    font-style: italic;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}
</style>

